import domready from 'domready';
import intlTelInput from 'intl-tel-input';
import { FORM_VALIDATOR_SETUP_EVENT } from '../../../webpack/src/just-validate/constants';
import { ExtendedGlobalConfigInterface, ExtendedJustValidate, JustValidateListenerElement } from '../../../webpack/src/just-validate/types';

/**
 * => countryCodeSelector : #countryCode
 * => defaultCountryCode : ''
 * => errorMessageSelector : .phone-error-message
 */

const TEL_INPUT_CSS_SELECTOR = 'input[type="tel"]'
const FORM_CSS_SELECTOR = '.frame.frame-type-form_formframework form'
const DEFAULT_COUNTRY_CODE_SELECTOR = '#countryCode'
const DEFAULT_ERROR_MESSAGE_SELECTOR = '.phone-error-message'
const DEFAULT_COUNTRY_CODE = ''
// Max time the loader stays visible if utils.js never answers.
const UTILS_LOADER_TIMEOUT_MS = 8000

// utils.js ships next to this bundle; resolved here because document.currentScript is only set while the bundle runs.
const UTILS_URL = (() => {
  const src = (document.currentScript as HTMLScriptElement | null)?.src
  return src ? new URL('utils.js', src).href : ''
})()

// utils.js (libphonenumber) exposes window.intlTelInputUtils; until then isValidNumber() returns null.
const isUtilsReady = (): boolean => !!(window as unknown as { intlTelInputUtils?: unknown }).intlTelInputUtils

// intl-tel-input only fetches utils.js on window.load (delayed by GTM tags, images...): start it now,
// only when the page has the phone field the wrapper will handle (same lookup as main()).
if (UTILS_URL && !isUtilsReady() && document.querySelector(FORM_CSS_SELECTOR)?.querySelector(TEL_INPUT_CSS_SELECTOR)) {
  window.intlTelInputGlobals.loadUtils(UTILS_URL)
}

interface InputTelInputValidatorProps {
  value: string,
  input: HTMLInputElement,
  telInput: intlTelInput.Plugin
}
interface InputTelInputWrapperInitOptions {
  countryCodeSelector: string,
  defaultCountryCode: string,
  inputCssSelector: string,
  formCssSelector: string;
  errorMessageSelector: string,
  waitingDomReady: boolean,
  validator: (props: InputTelInputValidatorProps) => boolean | undefined,
  intlTelInputOptions: intlTelInput.Options
  overrideSpecificConf?: Partial<ExtendedGlobalConfigInterface>
}

type QuerySelec = HTMLElement | Document

const getInputParentElement = (input: HTMLElement): HTMLElement | undefined => {
  if (input.classList.contains('input-parent-element')) {
    return input
  }
  else {
    const parentElement = input.parentElement
    if (parentElement) {
      return getInputParentElement(parentElement)
    } else {
      return undefined
    }
  }
}

const getJustValidateConfig = (element: HTMLElement) => {
  try {
    return JSON.parse(element.querySelector('.just-validate-config')?.textContent ?? '{}') as Partial<ExtendedGlobalConfigInterface>
  } catch (_) {
    return {}
  }
}

const getDefaultCountryCode = ({ countryCodeSelector, defaultCountryCode }: InputTelInputWrapperInitOptions) => {
  if (defaultCountryCode) {
    return defaultCountryCode
  }

  return (document.querySelector(countryCodeSelector) as HTMLInputElement)?.value ?? ''
}

const fetchCountryCode = async (options: InputTelInputWrapperInitOptions): Promise<string> => {
  const defCountryCode = getDefaultCountryCode(options)
  const eilApiKey = ''
  if (eilApiKey) {
    // Always fails because the API key is missing
    // See for proxy API
    try {
      const response = await fetch(`https://extreme-ip-lookup.com/json/?key=${eilApiKey}`)
      const jsonData = await response.json()
      return jsonData?.countryCode ?? defCountryCode
    } catch (_) {
      // silently fails
    }
  }
  return defCountryCode
}

const findErrorMessage = (elem: QuerySelec, form: QuerySelec, selector: string): string => {
  const pemElem = form.querySelector('.phone-error-message')
  if (!pemElem && elem !== form && elem !== document.body && elem.parentElement) {
    return findErrorMessage(elem.parentElement, form, selector)
  }

  return pemElem
    ? pemElem.getAttribute('data-error') ?? ''
    : ''
}

const initIntlTelInput = ({
  justvalidate,
  form,
  input,
  countryCode,
  options
}: {
  justvalidate: ExtendedJustValidate,
  form: HTMLFormElement
  input: HTMLInputElement,
  countryCode: string,
  options: InputTelInputWrapperInitOptions
}) => {
  const initialCountryCode = TYPO3.settings?.TS.countryIsoCode ? TYPO3.settings?.TS.countryIsoCode : countryCode
  const telInput = intlTelInput(input, {
    initialCountry: initialCountryCode,
    ...options.intlTelInputOptions
  })

  const errorMessage = findErrorMessage(input, form, options.errorMessageSelector)
  const errorMessageObj = errorMessage ? { errorMessage } : {}

  justvalidate.addRule(TEL_INPUT_CSS_SELECTOR, {
    // Sync validator (just-validate runs async ones only on submit); null = utils.js not loaded yet, so don't block.
    validator: () => {
      const result = options.validator({ input, value: input.value, telInput })
      return result == null ? true : !!result
    },
    ...errorMessageObj
  }, options.overrideSpecificConf)

  // Non-blocking loader until utils.js is there: the field stays usable (typing, browser autofill).
  if (!isUtilsReady()) {
    const itiWrapper = input.closest('.iti')
    const fieldKey = Object.keys(justvalidate.fields).find(key => justvalidate.fields[key].elem === input)
    itiWrapper?.classList.add('iti--loading-utils')
    const failSafe = setTimeout(() => itiWrapper?.classList.remove('iti--loading-utils'), UTILS_LOADER_TIMEOUT_MS)
    // telInput.promise resolves once utils.js is loaded (utilsScript option).
    telInput.promise
      .then(() => {
        // Errors are only displayed after a submit attempt: re-evaluate the tolerated number only then
        // (otherwise the next input event or the submit validates it with utils.js).
        if (fieldKey && justvalidate.isSubmitted && justvalidate.fields[fieldKey].isValid !== undefined) {
          return justvalidate.revalidateField(fieldKey)
        }
      })
      .catch(() => undefined)
      .finally(() => {
        clearTimeout(failSafe)
        itiWrapper?.classList.remove('iti--loading-utils')
      })
  }

  return telInput
}

interface MainOutput {
  telInput: intlTelInput.Plugin
  input: HTMLInputElement
}

const main = async (options: InputTelInputWrapperInitOptions): Promise<MainOutput> => {
  const form = document.querySelector(options.formCssSelector) as JustValidateListenerElement
  if (!form) {
    throw new Error(`Form element selected by ${options.formCssSelector} is missing`)
  }
  const input = form.querySelector(options.inputCssSelector) as HTMLInputElement
  if (!input) {
    throw new Error(`Input tel element selected by ${options.inputCssSelector} is missing`)
  }

  const parent = getInputParentElement(input)
  const globalConf = parent ? getJustValidateConfig(parent) : {}

  const countryCode = await fetchCountryCode(options)
  const mainParameters = {
    form,
    input,
    countryCode,
    options: {
      overrideSpecificConf: globalConf,
      ...options
    }
  }

  return new Promise((resolve) => {
    // If the justvalidate library is loaded and usable, using the global variable
    // else, waiting for the event
    if (global?.justvalidateInstance) {
      const telInput = initIntlTelInput({
        ...mainParameters,
        justvalidate: global.justvalidateInstance
      })
      resolve({ telInput, input })
    } else {
      form.addEventListener(FORM_VALIDATOR_SETUP_EVENT, e => {
        resolve({
          telInput: initIntlTelInput({
            ...mainParameters,
            justvalidate: e.detail.validate
          }),
          input
        })
      })
    }
  })
}

const defaultfInputTelInputWrapperInitOptions: InputTelInputWrapperInitOptions = {
  countryCodeSelector: DEFAULT_COUNTRY_CODE_SELECTOR,
  defaultCountryCode: DEFAULT_COUNTRY_CODE,
  inputCssSelector: TEL_INPUT_CSS_SELECTOR,
  formCssSelector: FORM_CSS_SELECTOR,
  errorMessageSelector: DEFAULT_ERROR_MESSAGE_SELECTOR,
  waitingDomReady: false,
  validator: ({ telInput }) => telInput.isValidNumber(),
  intlTelInputOptions: {
    formatOnDisplay: true,
    nationalMode: false,
    // Already loading (see loadUtils above); kept so telInput.promise waits for it, and as fallback for late tel fields.
    // Empty (bundle URL unknown): no utils.js, the tolerant validator then accepts the number.
    utilsScript: UTILS_URL
  }
}
export class InputTelInputWrapper {
  async init(options: Partial<InputTelInputWrapperInitOptions> = defaultfInputTelInputWrapperInitOptions): Promise<MainOutput> {
    const localOptions = {
      ...defaultfInputTelInputWrapperInitOptions,
      ...options,
      intlTelInputOptions: {
        ...defaultfInputTelInputWrapperInitOptions.intlTelInputOptions,
        ...options?.intlTelInputOptions
      }
    }
    if (localOptions.waitingDomReady) {
      return new Promise((resolve, reject) => {
        domready(async () => {
          try {
            const out = await main(localOptions)
            resolve(out)
          } catch (e) {
            reject(e)
          }
        })
      })
    }
    return main(localOptions)
  }
}

class InputTelInputWrapperInitEvent<T> extends CustomEvent<T> { }

const inputTelInputWrapperInstance = new InputTelInputWrapper()
const newEvent = new InputTelInputWrapperInitEvent('intlinputwrapper-setup', { detail: { inputTelInputWrapperInstance } })
document.dispatchEvent(newEvent)

global.inputTelInputWrapperInstance = inputTelInputWrapperInstance
